import { formatCurrency } from '../currency';
import { Currency } from '../../constants';
import {
  BMSM_MILESTONES_META,
  type MilestoneMeta,
  type Threshold,
  type CampaignMilestone,
} from '../../constants/campaign/bmsm';

export function getThresholdByKey(key: string, thresholds: Threshold): number | undefined {
  const value = (thresholds as unknown as Record<string, unknown>)[key];
  return typeof value === 'number' ? (value as number) : undefined;
}

export function getActiveMilestoneMetas(_: Threshold): readonly MilestoneMeta[] {
  return BMSM_MILESTONES_META;
}

export function buildCampaignMilestones(
  thresholds: Threshold,
  cartTotal: number,
  currency: Currency,
): CampaignMilestone[] {
  const metas = getActiveMilestoneMetas(thresholds);
  return metas.map((meta) => {
    const threshold = getThresholdByKey(meta.key, thresholds);
    if (typeof threshold !== 'number') {
      return {
        key: meta.key,
        title: meta.title,
        threshold: Number.POSITIVE_INFINITY,
        achieved: false,
        statusText: 'N/A',
        anchorPercent: meta.anchorPercent,
      } as CampaignMilestone;
    }
    const achieved = cartTotal >= threshold;
    const statusText = achieved ? 'Available' : formatCurrency(currency, threshold);
    return { key: meta.key, title: meta.title, threshold, achieved, statusText, anchorPercent: meta.anchorPercent };
  });
}
function interpolatePercent(points: Array<{ threshold: number; percent: number }>, x: number): number {
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    if (x < curr.threshold) {
      const range = curr.threshold - prev.threshold;
      if (range <= 0) {
        return curr.percent;
      }
      const ratio = (x - prev.threshold) / range;
      return prev.percent + ratio * (curr.percent - prev.percent);
    }
  }
  return 100;
}

// threshold is reached, progress snaps to 100% regardless of its anchor.
export function computeCampaignProgressAnchored(thresholds: Threshold, cartTotal: number): number {
  const metas = getActiveMilestoneMetas(thresholds);

  // Determine the highest available numeric threshold among metas
  const numericThresholds: Array<{ threshold: number; percent: number }> = [];
  metas.forEach((meta) => {
    const t = getThresholdByKey(meta.key, thresholds);
    if (typeof t === 'number') {
      numericThresholds.push({ threshold: t, percent: meta.anchorPercent * 100 });
    }
  });

  if (numericThresholds.length === 0) {
    return 0;
  }

  const maxThreshold = numericThresholds.reduce((max, curr) => (curr.threshold > max ? curr.threshold : max), 0);
  if (cartTotal >= maxThreshold) {
    return 100;
  }

  const points: Array<{ threshold: number; percent: number }> = [{ threshold: 0, percent: 0 }, ...numericThresholds];
  points.sort((a, b) => a.threshold - b.threshold);
  points.push({ threshold: Number.POSITIVE_INFINITY, percent: 100 });

  return interpolatePercent(points, cartTotal);
}

export function computeCampaignProgress(thresholds: Threshold, cartTotal: number): number {
  // Generalized: reuse anchor-based progress which supports any number of milestones
  return computeCampaignProgressAnchored(thresholds, cartTotal);
}
